/**
 * What an exercise's materials may be — the browser's mirror of the backend's one link gate.
 *
 * **The server is the authority and this is a mirror, deliberately.** `ExternalLinkPolicy` in
 * `train-with-me/Modules/Exercises/Domain/ExternalLinkPolicy.cs` is requirement R12 and it is the
 * only place a link is actually accepted or refused; every write path goes through it and answers
 * `probs/validation-error` with a sentence. What this module buys is the trainer finding out while
 * they are still typing, and the editor being able to say WHICH rule refused them rather than
 * echoing a server sentence composed in another language.
 *
 * Mirrored rule for rule, in the server's own order, so a divergence is a diff rather than a hunt:
 *
 * | Rule | Server | Here |
 * |---|---|---|
 * | An address is required | `ValidateAddress` | `missingAddress` |
 * | At most 2048 characters | `MaxUrlLength` | `addressTooLong` |
 * | Absolute, and parseable | `Uri.TryCreate(Absolute)` | `notAnAddress` |
 * | `javascript:` refused BY NAME | `IsScheme(javascript)` | `scriptScheme` |
 * | `data:` refused BY NAME | `IsScheme(data)` | `scriptScheme` |
 * | A host is required | checked BEFORE https | `noHost` |
 * | https only, never upgraded | `IsScheme(https)` | `notHttps` |
 * | No credentials in the address | `UserInfo` | `hasCredentials` |
 * | A label of at most 80 characters | `MaxLabelLength` | `labelTooLong` |
 * | At most 3 DISTINCT destinations | `MaxLinks`, after de-duplication | `tooManyLinks` |
 *
 * **The host check is ordered before the scheme check, and that order is load-bearing.** A
 * schemed-but-hostless address — `about:blank`, `tel:123`, `urn:x` — is also not https, so testing
 * the scheme first would refuse it for the wrong reason and tell the trainer to type `https://`
 * when the problem is that there is nowhere to go. The server's own comment says so.
 *
 * **The cap counts DESTINATIONS, not entries.** Two spellings of one address (a capitalised host,
 * an explicit default port) are one place to go, so they collapse before three is counted — which
 * is why a trainer who pastes the same video twice has used one of their three, not two. `URL`'s
 * `href` is the normalising parse here, as `Uri.AbsoluteUri` is there.
 *
 * **What is NOT mirrored, and why.** The server also probes `Uri.IdnHost` and refuses a host IDNA
 * cannot encode — an invisible joiner surviving a paste, a name past the DNS length limit. There is
 * no browser equivalent that answers the same question (`URL` silently punycodes what it can and
 * accepts what .NET rejects), so a fabricated approximation would refuse addresses the server
 * accepts. That one rule is left to the server, and its `detail` is what the trainer reads.
 */

/** Cap on DISTINCT destinations. `ExternalLinkPolicy.MaxLinks`. */
export const MAX_EXERCISE_LINKS = 3;

/** `ExternalLinkPolicy.MaxUrlLength`. */
export const MAX_LINK_URL_LENGTH = 2048;

/** `ExternalLinkPolicy.MaxLabelLength`. */
export const MAX_LINK_LABEL_LENGTH = 80;

/** Why one submitted link was refused. One member per rule, so copy is chosen and never composed. */
export type LinkRefusal =
  | 'missingAddress'
  | 'addressTooLong'
  | 'notAnAddress'
  | 'scriptScheme'
  | 'noHost'
  | 'notHttps'
  | 'hasCredentials'
  | 'labelTooLong'
  | 'tooManyLinks';

/** One link as the trainer typed it. */
export interface SubmittedLink {
  readonly url: string;
  readonly label: string;
}

/** An accepted link, trimmed, with the destination the cap was counted on. */
export interface AcceptedLink {
  readonly url: string;
  readonly label: string;
  /** The normalised address — `URL.href`. Two spellings of one place share it. */
  readonly destination: string;
  /** The host as it should read, for the brand mark and the row's second line. */
  readonly displayHost: string;
}

export type LinkCheck =
  | { readonly ok: true; readonly link: AcceptedLink }
  | { readonly ok: false; readonly refusal: LinkRefusal };

/** The two schemes the server refuses by name, because that is the refusal a trainer must read. */
const SCRIPT_SCHEMES: readonly string[] = ['javascript:', 'data:'];

/**
 * One address and one label against the policy, with no knowledge of what else is in the list.
 *
 * The cap is `checkExerciseLinks`'s, below: it is the only rule that depends on more than one link.
 */
export function checkExerciseLink(submitted: SubmittedLink): LinkCheck {
  const url = submitted.url.trim();
  const label = submitted.label.trim();

  if (url === '') return { ok: false, refusal: 'missingAddress' };
  if (url.length > MAX_LINK_URL_LENGTH) return { ok: false, refusal: 'addressTooLong' };

  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    // `URL` refuses a relative address outright, which is the server's "must be absolute".
    return { ok: false, refusal: 'notAnAddress' };
  }

  // By name, and before everything else: a trainer who pasted a `javascript:` payload is told what
  // was wrong with it rather than told to use https.
  if (SCRIPT_SCHEMES.includes(parsed.protocol.toLowerCase())) {
    return { ok: false, refusal: 'scriptScheme' };
  }

  // BEFORE the https check. See the header: `about:`, `tel:` and `urn:` are not https either, and
  // refusing them for that reason names the wrong problem.
  if (parsed.hostname === '') return { ok: false, refusal: 'noHost' };

  // Never quietly upgraded. The trainer typed a destination that is not the one that would be
  // stored, and they get to see that rather than discover it later.
  if (parsed.protocol.toLowerCase() !== 'https:') return { ok: false, refusal: 'notHttps' };

  if (parsed.username !== '' || parsed.password !== '') {
    return { ok: false, refusal: 'hasCredentials' };
  }

  if (label.length > MAX_LINK_LABEL_LENGTH) return { ok: false, refusal: 'labelTooLong' };

  return {
    ok: true,
    link: { url, label, destination: parsed.href, displayHost: parsed.hostname },
  };
}

/** What a whole submitted list resolves to. */
export interface LinkListCheck {
  /** Everything accepted, duplicates collapsed, in the order they were submitted. */
  readonly accepted: readonly AcceptedLink[];
  /** The FIRST refusal, and the index it happened at. `null` when the whole list is legal. */
  readonly refused: { readonly at: number; readonly refusal: LinkRefusal } | null;
}

/**
 * A whole list against the policy, including the cap.
 *
 * Stops at the first refusal and names where it was, which is what an editor needs to put the
 * message on the row that caused it. The server does the same — it throws naming the first entry it
 * refuses — so a list this accepts and a list it refuses are the same two lists on both sides.
 *
 * A duplicate destination is NOT a refusal. It is dropped, exactly as the server drops it, so a
 * trainer who pasted the same address twice has three places to go and not two.
 */
export function checkExerciseLinks(submitted: readonly SubmittedLink[]): LinkListCheck {
  const accepted: AcceptedLink[] = [];
  const destinations = new Set<string>();

  for (const [at, entry] of submitted.entries()) {
    const check = checkExerciseLink(entry);
    if (!check.ok) return { accepted, refused: { at, refusal: check.refusal } };
    if (destinations.has(check.link.destination)) continue;
    destinations.add(check.link.destination);
    accepted.push(check.link);
    // Inside the loop, as the server does it: an absurdly long submission is refused on its fourth
    // distinct destination rather than after every entry has been parsed.
    if (accepted.length > MAX_EXERCISE_LINKS) {
      return { accepted: accepted.slice(0, MAX_EXERCISE_LINKS), refused: { at, refusal: 'tooManyLinks' } };
    }
  }

  return { accepted, refused: null };
}

/** The three sources a material can wear its own mark for. Everything else is a neutral link. */
export type LinkBrandKey = 'youtube' | 'drive' | 'instagram';

/**
 * The domains each mark stands for, matched on a LABEL BOUNDARY and never as a substring.
 *
 * A mark is a claim about who is on the other end of a link, so the test has to be one an attacker
 * cannot satisfy by naming their own host after somebody else's. `host.includes('youtube.')` gave
 * `myyoutube.com` YouTube's mark, and `host.includes('drive.google.')` gave it to
 * `drive.google.com.evil.net` — in both cases beside a `displayHost` the trainer would read as the
 * real place. The host must now BE one of these or sit under it.
 *
 * Registrable domains rather than stems: `youtube.<anything>` is what let the second spoof through,
 * because everything after the brand's name is attacker-chosen. The cost is that a country domain
 * this table does not list (`youtube.pl`) draws the neutral link glyph, which is the safe way to be
 * wrong — a missing mark understates, a borrowed one lies.
 */
const BRAND_DOMAINS: readonly (readonly [LinkBrandKey, string])[] = [
  ['youtube', 'youtube.com'],
  ['youtube', 'youtube-nocookie.com'],
  ['youtube', 'youtu.be'],
  ['drive', 'drive.google.com'],
  ['drive', 'docs.google.com'],
  ['instagram', 'instagram.com'],
];

/**
 * Which brand mark a link's row draws, from the host the SERVER resolved.
 *
 * `displayHost` rather than the raw address, because that is the spelling the server says a human
 * should read and the one printed on the row — so the mark and the words underneath it can never
 * name two different places. A host this build has never heard of gets the neutral link glyph,
 * which is why the answer is nullable rather than falling back to one of the three.
 */
export function brandKeyOf(displayHost: string): LinkBrandKey | null {
  // A trailing dot is a legal, fully-qualified spelling of the same host, and dropping it here
  // means `youtube.com.` is not a hole in `BRAND_DOMAINS`.
  const host = displayHost.trim().toLowerCase().replace(/\.$/, '');
  const found = BRAND_DOMAINS.find(
    ([, domain]) => host === domain || host.endsWith(`.${domain}`),
  );
  return found?.[0] ?? null;
}

/**
 * Which sentence a refusal reads as, as a KEY.
 *
 * Here beside the policy since the creator became its second reader: the theatre's materials editor
 * and the creator's links editor refuse the same nine rules and must word them the same way.
 *
 * Every one of them is the MOBILE locale's, verbatim — `exerciseLibrary.links.error.*`. The phone
 * already refuses these nine rules in these nine sentences and a trainer who meets the same wall on
 * two devices should read the same words; a web-only paraphrase would be a second wording of one
 * rule, which is the duplication the locale split exists to avoid.
 *
 * ── `scriptScheme` is ONE refusal here and TWO sentences there ──────────────────────────────
 * The policy refuses `javascript:` and `data:` by name, in one rule, because they are one thing —
 * an address that is not a place. The phone words them separately, and naming the wrong one would
 * be the app telling a trainer that the payload they pasted was something else. So the address
 * decides which sentence, and the address is the only thing it decides.
 */
export function linkRefusalKey(refusal: LinkRefusal, url: string): string {
  const errors = 'exerciseLibrary.links.error';
  switch (refusal) {
    case 'missingAddress':
      return `${errors}.required`;
    case 'addressTooLong':
      return `${errors}.urlTooLong`;
    case 'notAnAddress':
      return `${errors}.notAbsolute`;
    case 'scriptScheme':
      return url.trim().toLowerCase().startsWith('data:')
        ? `${errors}.dataScheme`
        : `${errors}.javascriptScheme`;
    case 'noHost':
      return `${errors}.noHost`;
    case 'notHttps':
      return `${errors}.notHttps`;
    case 'hasCredentials':
      return `${errors}.hasCredentials`;
    case 'labelTooLong':
      return `${errors}.labelTooLong`;
    case 'tooManyLinks':
      return `${errors}.tooMany`;
  }
}
